import React, { useEffect, useRef } from "react";
import Button from "../../components/ui/Button";
// Import GSAP and ScrollTrigger
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const GivingBackSection = () => {
  // 1. Create refs for the section, content, and images
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const mainImageRef = useRef(null);
  const droppedImageRef = useRef(null);

  useEffect(() => {
    // 2. Set up GSAP context
    const context = gsap.context(() => {
      // Define the common ScrollTrigger settings for the section's overall entry
      const triggerSettings = {
        trigger: sectionRef.current,
        start: "top 85%", // Animation starts when section hits 85% down the viewport
        end: "center 50%", // Animation completes by the time the section is near the center
        scrub: 1, // Smooth scrubbing effect
        // markers: true, // Uncomment for debugging
      };

      // ----------------------------------------------------
      // A. Main Image Animation (Slide Up from Bottom Right)
      // ----------------------------------------------------
      if (mainImageRef.current) {
        gsap.from(mainImageRef.current, {
          y: 100, // Start 100px below
          opacity: 0,
          ease: "power2.out",
          scrollTrigger: triggerSettings,
        });
      }

      // ----------------------------------------------------
      // B. Dropped Image Animation (Slide Up and Fade)
      // ----------------------------------------------------
      if (droppedImageRef.current) {
        gsap.from(droppedImageRef.current, {
          y: 50, // Start 50px below
          opacity: 0,
          ease: "power1.out",
          scrollTrigger: triggerSettings,
        });
      }

      // ----------------------------------------------------
      // C. Left Content Block Animation (Slide in from Left)
      // ----------------------------------------------------
      if (contentRef.current) {
        gsap.from(contentRef.current, {
          x: -100, // Start 100px off to the left
          opacity: 0,
          ease: "power2.out",
          scrollTrigger: triggerSettings,
        });
      }

      // ----------------------------------------------------
      // D. Inner Text Reveal Animation (Staggered Reveal)
      // ----------------------------------------------------
      // We target the individual elements inside the content block
      const textElements = gsap.utils.toArray(
        contentRef.current.querySelectorAll(".giving-back-text-item"),
      );

      const innerTriggerSettings = {
        trigger: contentRef.current, // Use the content block as the trigger
        start: "top 85%",
        end: "center 60%",
        scrub: 1,
      };

      textElements.forEach((element, index) => {
        // Initial State: Hidden (opacity: 0), slightly down (y: 20), clipped
        gsap.set(element, {
          y: 20,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        gsap.to(element, {
          y: 0, // Final state: Original position (move up)
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)", // Fully visible
          duration: 1,
          ease: "power2.out",
          delay: index * 0.1, // Stagger effect for title, paragraph, and button
          scrollTrigger: innerTriggerSettings,
        });
      });
    }, sectionRef);

    // 3. Cleanup
    return () => context.revert();
  }, []);

  return (
    <section
      ref={sectionRef} // Attach section ref
      className="w-full relative bg-[#195939] overflow-hidden h-[90vh] md:h-[85vh] lg:h-[100vh]"
    >
      <div className="relative h-full">
        <div
          ref={contentRef} // Attach content ref
          className="flex flex-col gap-[12px] sm:gap-[18px] xl:gap-[24px] px-4 sm:px-6 2xl:px-[150px] pt-[75px] w-[100%] md:w-[66%] 2xl:w-[55%]"
        >
          {/* Header - Added giving-back-text-item class */}
          <h2 className="giving-back-text-item giving-back-text-item-2 text-[30px] md:text-[60px] 2xl:text-[100px] font-bold font-['Parkinsans'] text-left text-white">
            Giving Back To
            <br />
            Nature
          </h2>

          {/* Paragraph - Added giving-back-text-item class */}
          <p className="giving-back-text-item text-[16px] sm:text-[20px] xl:text-[24px] font-light font-['Poppins'] leading-[25px] sm:leading-[32px] xl:leading-[38px] text-justify text-white md:w-[80%]">
            Nature works in a continuous cycle where waste becomes nourishment.
            Produced by Asia’s largest biogas plant, Govardhannathji Energies
            LLP, UTPANN organic fertilizer completes this natural cycle by
            transforming organic waste into a safe, nutrient-rich alternative to
            traditional cow-dung fertilizer. This responsible approach supports
            soil regeneration, protects public health, and ensures
            environmentally sound waste management.
          </p>

          {/* Button - Wrapped button in a div and added giving-back-text-item class */}
          {/* <div className="giving-back-text-item mb-[121px] sm:mb-[182px] xl:mb-[242px]">
            <Button
              text="Learn More"
              text_font_family="Parkinsans"
              text_font_weight="font-semibold"
              text_line_height="leading-[23px] sm:leading-[28px] xl:leading-[34px]"
              text_text_align="center"
              border_border="1px solid #ffffff"
              border_border_radius="rounded-[30px]"
              layout_width="auto"
              text_font_size="text-[16px] sm:text-[20px] xl:text-[24px]"
              padding="t=12px,r=32px,b=12px,l=32px"
              className="hover:bg-white hover:text-[#195939] text-[#FFF] transition-colors duration-300"
            />
          </div> */}
        </div>
        <div>
          {/* Main Image - Attach ref */}
          <div ref={mainImageRef} className="absolute right-0 bottom-0 z-10 ">
            <img
              src="/assets/AboutPageImg/Giving.png"
              alt="Organic farming landscape"
              className="image-giving-wrapper sm:w-[70%] md:w-[45%] lg:w-[50%] xl:w-[70%] 2xl:w-[100%] h-auto ml-auto"
            />
          </div>
          {/* Dropped Image - Attach ref */}
          <div
            ref={droppedImageRef}
            className="absolute hidden xl:block right-[-400px] bottom-[-150px] "
          >
            <img
              src="/assets/AboutPageImg/dropped.png"
              alt="Organic farming landscape"
              className="image-giving-wrapper sm:w-[70%]  md:w-[45%] lg:w-[50%] xl:w-[70%] 2xl:w-[100%] h-auto ml-auto"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default GivingBackSection;
